import { z } from 'zod';

export type Party = {
  id: string;
  name: string;
  short_code: string;
  type: string;
  side: 'internal' | 'external';
  parent_id?: string | null;
  country?: string | null;
  lei?: string | null;
  base_currency?: string | null;
  is_active: boolean;
  children: Party[];
};

export const PartyTypeSchema = z.string().min(1);
export const PartySideSchema = z.enum(['internal', 'external']);

export const PartySchema: z.ZodType<Party> = z.lazy(() =>
  z.object({
    id: z.string().min(1),
    name: z.string(),
    short_code: z.string(),
    type: PartyTypeSchema,
    side: PartySideSchema,
    parent_id: z.string().min(1).nullable().optional(),
    country: z.string().nullable().optional(),
    lei: z.string().nullable().optional(),
    base_currency: z.string().nullable().optional(),
    is_active: z.boolean(),
    children: z.array(PartySchema),
  }),
);

export const PartyListSchema = z.array(PartySchema);

export const CreatePartyRequestSchema = z.object({
  name: z.string().min(1).max(200),
  short_code: z.string().min(1).max(30),
  type: PartyTypeSchema,
  side: PartySideSchema,
  parent_id: z.string().min(1).optional(),
  country: z.string().min(2).max(3).optional(),
  lei: z.string().max(20).optional(),
  base_currency: z.string().length(3).optional(),
});

export const PartyHierarchyPayloadSchema = z.object({
  selected_party_id: z.string().min(1).optional(),
});

export type CreatePartyRequest = z.infer<typeof CreatePartyRequestSchema>;
export type PartyHierarchyPayload = z.infer<typeof PartyHierarchyPayloadSchema>;

export function flattenPartyTree(parties: Party[]): Party[] {
  const flat: Party[] = [];
  const visit = (party: Party) => {
    flat.push(party);
    for (const child of party.children) {
      visit(child);
    }
  };

  for (const party of parties) {
    visit(party);
  }

  return flat;
}
